import { cn } from '@/lib/utils';

interface PasswordStrengthIndicatorProps {
  password: string;
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const strengthChecks = [
    { regex: /.{8,}/, label: '8+ Characters' },
    { regex: /[A-Z]/, label: 'Uppercase' },
    { regex: /[a-z]/, label: 'Lowercase' },
    { regex: /[0-9]/, label: 'Number' },
  ];

  const getPasswordStrength = () => {
    let score = 0;
    strengthChecks.forEach((check) => {
      if (check.regex.test(password)) {
        score += 1;
      }
    });
    
    if (score === 0) return { level: 0, label: '', color: '' };
    if (score < 2) return { level: 1, label: 'Weak', color: 'bg-red-500' };
    if (score < 4) return { level: 2, label: 'Medium', color: 'bg-yellow-500' };
    return { level: 3, label: 'Strong', color: 'bg-green-500' };
  };

  const strength = getPasswordStrength();

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center justify-between">
        <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full transition-all duration-300",
              strength.color,
              strength.level === 0 ? "w-0" : 
              strength.level === 1 ? "w-1/3" : 
              strength.level === 2 ? "w-2/3" : 
              "w-full"
            )}
          />
        </div>
        <span className="text-xs font-medium ml-3 min-w-16">
          {strength.label}
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-xs">
        {strengthChecks.map((check, index) => (
          <div
            key={index}
            className={cn(
              "flex items-center gap-1",
              check.regex.test(password)
                ? "text-foreground"
                : "text-muted-foreground"
            )}
          >
            <div 
              className={cn(
                "w-1.5 h-1.5 rounded-full",
                check.regex.test(password)
                  ? strength.color
                  : "bg-muted-foreground"
              )} 
            />
            <span>{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}